import { FC, useMemo } from 'react';
import {
  Button,
  Card,
  Divider,
  Input,
  RadixTable,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TColumn,
} from '@features/ui';
import Pagination from '@features/ui/components/pagination';
import { TActiveLifetimePAData, TActiveLifetimePAParams } from '@src/types';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface PaTradersWithExcessiveDailyProfitTableProps {
  data: TActiveLifetimePAData[];
  filters: TActiveLifetimePAParams;
  isLoading: boolean;
  totalPages: number;
  onFiltersChange: (filters: TActiveLifetimePAParams) => void;
  onGetData: () => void;
  onPageChange: (page: number) => void;
}

export const PaTradersWithExcessiveDailyProfitTable: FC<
  PaTradersWithExcessiveDailyProfitTableProps
> = ({
  data,
  filters,
  isLoading,
  totalPages,
  onFiltersChange,
  onGetData,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const dayOptions = ['7', '10', '30'];

  const handleFilterChange = (
    key: keyof TActiveLifetimePAParams,
    value: number | undefined
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleGet = () => {
    onGetData();
  };

  const columns = useMemo(
    (): TColumn<TActiveLifetimePAData>[] => [
      {
        title: 'Account Type',
        render: (item) => <p>{item?.account_type ?? '-'}</p>,
      },
      {
        title: 'Full Name',
        render: (item) => <p>{item?.full_name ?? '-'}</p>,
      },
      {
        title: 'Username',
        render: (item) => (
          <p
            className={'text-blue cursor-pointer'}
            onClick={() => navigate(`/admin/users/${item?.user_id}`)}
          >
            {item?.username ?? '-'}
          </p>
        ),
      },
      {
        title: 'Account ID',
        className: 'text-center',
        render: (item) => <p>{item?.account_id ?? '-'}</p>,
      },
      {
        title: 'Rithmic Account',
        render: (item) => <p>{item?.rithmic_account_name ?? '-'}</p>,
      },
      {
        title: 'Package',
        render: (item) => <p>{item?.package_name ?? '-'}</p>,
      },
      {
        title: 'Valid From',
        render: (item) =>
          item?.valid_from ? (
            <p>{format(new Date(item.valid_from), 'MMM dd, yyyy')}</p>
          ) : (
            <p>-</p>
          ),
      },
      {
        title: 'Valid To',
        render: (item) =>
          item?.valid_to ? (
            <p>{format(new Date(item.valid_to), 'MMM dd, yyyy')}</p>
          ) : (
            <p>-</p>
          ),
      },
      {
        title: 'Trading Days (Period)',
        className: 'text-center',
        render: (item) => <p>{item?.trading_days_in_period ?? 0}</p>,
      },
      {
        title: 'Total Trading Days',
        className: 'text-center',
        render: (item) => <p>{item?.total_trading_days ?? 0}</p>,
      },
      {
        title: 'ALTV',
        className: 'text-right',
        render: (item) => <p>${item?.altv ?? '0.00'}</p>,
      },
      {
        title: 'Account Balance',
        className: 'text-right',
        render: (item) => <p>${item?.account_balance ?? '0.00'}</p>,
      },
      {
        title: 'Balance Date',
        render: (item) =>
          item?.account_balance_date ? (
            <p>{format(new Date(item.account_balance_date), 'MMM dd, yyyy')}</p>
          ) : (
            <p>-</p>
          ),
      },
      {
        title: 'Total Accounts',
        className: 'text-center',
        render: (item) => <p>{item?.total_account ?? 0}</p>,
      },
    ],
    [navigate]
  );

  return (
    <Card className={'mt-4.5'}>
      <div className={'flex lg:flex-row flex-col justify-between mb-5 gap-6'}>
        <div className={'flex gap-6 flex-wrap items-end'}>
          <div className={'flex flex-col gap-1'}>
            <label className={'text-sm text-muted-foreground'}>
              Not Traded Days
            </label>
            <Input
              className={'w-[200px]'}
              inputSize={'sm'}
              placeholder={'Enter days'}
              type={'number'}
              value={filters.not_traded_days ?? ''}
              onChange={(e) =>
                handleFilterChange(
                  'not_traded_days',
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
            />
          </div>
          <div className={'flex flex-col gap-1'}>
            <label className={'text-sm text-muted-foreground'}>
              In Period Days
            </label>
            <Select
              value={filters.in_period_days?.toString()}
              onValueChange={(value) =>
                handleFilterChange('in_period_days', Number(value))
              }
            >
              <SelectTrigger className={'w-[200px] h-7.5'}>
                <SelectValue placeholder={'Select days'} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {dayOptions.map((option) => (
                    <SelectItem key={`period-${option}`} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className={'flex gap-5 items-end'}>
          <Button
            disabled={isLoading}
            size={'sm'}
            variant={'lime'}
            onClick={handleGet}
          >
            {isLoading ? 'Loading...' : 'Get'}
          </Button>
        </div>
      </div>
      <Divider className={'mb-1'} />
      {isLoading ? (
        <div className={'flex justify-center items-center py-10'}>
          <p className={'text-muted-foreground'}>Loading...</p>
        </div>
      ) : (
        <>
          <RadixTable columns={columns} data={data} />
          {totalPages > 1 && (
            <>
              <Divider />
              <Pagination
                className={'mt-6'}
                current={filters.page ?? 1}
                total={totalPages}
                onChange={onPageChange}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
};
